import { useState } from 'react';
import PhotoUploadWidget from '../common/photoupload/PhotoUploadWidget';
import PhotoUploadOverlay from '../common/photoupload/PhotoUploadOverlay';
import type { UploadedPhotoPlaceholderProps } from '../common/photoupload/types.d.ts';
import { Svg } from '../common/Svg';

interface ClassroomPhotoUploadContentProps {
    onPhotoUpload: (files: FileList | File[]) => void,
    uploadedPhoto: UploadedPhotoPlaceholderProps | null,
    onDeleteRequest: (photoId: string) => void,
    minImageWidth: number,
    minImageHeight: number,
    errorMessage: string | null,
    clearErrorMessage: () => void,
}

const EXAMPLE_PHOTOS = [
    { src: `${secureBaseURL}images/teacher/example-classroom-photos/1.jpg`, alt: 'A group of students in a band' },
    { src: `${secureBaseURL}images/teacher/example-classroom-photos/2.jpg`, alt: 'Colorful acrylic landscape painting' },
    { src: `${secureBaseURL}images/teacher/example-classroom-photos/3.jpg`, alt: 'Students in a lab' },
    { src: `${secureBaseURL}images/teacher/example-classroom-photos/4.jpg`, alt: 'Student hands working with playdoh' },
];

const ClassroomPhotoUploadContent = ({
    onPhotoUpload,
    uploadedPhoto,
    onDeleteRequest,
    minImageWidth,
    minImageHeight,
    errorMessage,
    clearErrorMessage,
}: ClassroomPhotoUploadContentProps) => {
    const [isCertified, setIsCertified] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const isUploading = uploadedPhoto !== null && uploadedPhoto.isLoading;

    const handleFileSelected = (files: FileList | File[]) => {
        const file = Array.from(files)[0];
        if (file) {
            clearErrorMessage();
            setPendingFile(file);
        }
    };

    const handleUploadClick = () => {
        if (pendingFile) {
            onPhotoUpload([pendingFile]);
        }
    };

    const handleDeletePending = () => {
        setPendingFile(null);
        setIsCertified(false);
    };

    const renderPhotoUploadWidgetOrOverlay = () => {
        if (uploadedPhoto) {
            return (
                <PhotoUploadOverlay
                    photoUrl={uploadedPhoto.photoUrl}
                    shouldRender={uploadedPhoto.shouldRender}
                    shouldRenderLoadingDots={uploadedPhoto.isLoading}
                    photoIndex={uploadedPhoto.photoId}
                    onDeleteRequest={onDeleteRequest}
                />
            );
        }
        // This shows a preview a selected-but-not-uploaded photo. Skip if the previous upload failed because we want
        // them to upload another in that case.
        if (pendingFile && !errorMessage) {
            return (
                <PhotoUploadOverlay
                    photoUrl={URL.createObjectURL(pendingFile)}
                    shouldRender={true}
                    shouldRenderLoadingDots={false}
                    photoIndex="pending"
                    onDeleteRequest={handleDeletePending}
                    minWidth={minImageWidth}
                    minHeight={minImageHeight}
                />
            );
        }
        return (
            <PhotoUploadWidget
                onPhotoUpload={handleFileSelected}
                multiple={false}
            />
        );
    };

    const renderExamplePhotos = () => {
        return <div className="good-examples">
            {EXAMPLE_PHOTOS.map((photo) => (
                <img key={photo.src} src={photo.src} alt={photo.alt}/>
            ))}
            <p>
                If your photo features students, make sure no student&rsquo;s face takes up
                more than &frac14; of the photo.
            </p>
            <div className="see-examples">
                <Svg icon="star" attributes={{ role: 'presentation' }}/>
                <a target="_blank" rel="noopener noreferrer"
                    href={`${secureBaseURL}teacher/classroom-photo/examples.html`}>
                    See more good examples
                </a>
                <Svg icon="star" attributes={{ role: 'presentation' }}/>
            </div>
        </div>;
    };

    const renderPermissionSlipCheckbox = () => {
        return <div className="check-permissions">
            <div className="checkbox-holder">
                <input
                    type="checkbox"
                    className="js-photos-certified-checkbox"
                    id="photosCertified"
                    checked={isCertified}
                    disabled={!pendingFile && !uploadedPhoto}
                    onChange={(e) => setIsCertified(e.target.checked)}
                />
                <label htmlFor="photosCertified">
                    I have signed{' '}
                    <a target="_blank" rel="noopener noreferrer" href={`${secureBaseURL}docs/permission_slip.pdf`}>
                        permission slips
                    </a>{' '}
                    for every student in this photo. I understand that DonorsChoose may request
                    to collect the slips if this photo will be featured outside of my project.
                </label>
            </div>
            {errorMessage && <div className="alert-message warning">{errorMessage}</div>}
            <button
                className="button-primary upload-button"
                disabled={!pendingFile || !isCertified || isUploading}
                type="button"
                onClick={handleUploadClick}
            >
                Upload
            </button>
        </div>;
    };

    return (
        <div className="classroom-photo-upload-content">
            <h1>Show us your classroom&rsquo;s spirit</h1>
            <p className="subheader">
                Choose a student group photo or a vibrant photo of student work.
            </p>

            {renderPhotoUploadWidgetOrOverlay()}

            <div className="helper-text">
                <span className="text-discreet">
                    JPEG, PNG, or HEIC at least {minImageWidth} &times; {minImageHeight} pixels.
                </span>
                <span className="text-discreet">No emojis, clipart, or stock photos.</span>
            </div>

            {renderPermissionSlipCheckbox()}

            {renderExamplePhotos()}
        </div>
    );
};

export default ClassroomPhotoUploadContent;
